import { useEffect, useState } from 'react';
import { Autocomplete, TextField, Box, CircularProgress, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { eventService } from '../../../shared/services/tenant/eventService';
import { useAuth } from '../../auth/context/AuthContext';
import { UserRole } from '../../../utils/userMapper';

interface EventOption {
  id: string;
  title: string;
}

function useDebouncedValue<T>(value: T, delay = 400) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debounced;
}

export function EventSelector({
  eventId,
  setEventId,
  onSelectEvent,
}: {
  eventId: string;
  setEventId: (id: string) => void;
  onSelectEvent?: (event: EventOption | null) => void;
}) {
  const { user } = useAuth();
  const [inputValue, setInputValue] = useState('');
  const [selectedOption, setSelectedOption] = useState<EventOption | null>(null);
  const debouncedSearch = useDebouncedValue(inputValue, 400);
  const isOrganizer = user?.role === UserRole.ORGANIZER;

  const { data: options = [], isFetching, isLoading } = useQuery({
    queryKey: ['events-select', isOrganizer, debouncedSearch],
    queryFn: async () => {
      const res = await eventService.listForSelect(1, 30, {
        title: debouncedSearch.trim() || undefined,
        organizer: isOrganizer,
      });
      return (res.data?.data || []) as EventOption[];
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!eventId) {
      setSelectedOption(null);
      return;
    }
    const match = options.find(event => event.id === eventId);
    if (match) {
      setSelectedOption(match);
    }
  }, [eventId, options]);

  const loading = isLoading || isFetching;

  return (
    <Box sx={{
      mb: 3,
      width: '100%',
      maxWidth: '100%',
      overflow: 'hidden'
    }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Selecionar Evento
      </Typography>
      <Autocomplete
        options={options}
        filterOptions={items => items}
        getOptionLabel={option => option.title || ''}
        value={selectedOption}
        loading={loading}
        onInputChange={(_, newInput) => setInputValue(newInput)}
        onChange={(_, value) => {
          setSelectedOption(value);
          setEventId(value ? value.id : '');
          if (onSelectEvent) onSelectEvent(value);
        }}
        sx={{
          width: '100%',
          maxWidth: '500px',
          '& .MuiAutocomplete-inputRoot': {
            width: '100%',
            maxWidth: '100%',
          },
          '& .MuiInputBase-input': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            width: '100%',
          },
          '& .MuiAutocomplete-endAdornment': {
            position: 'absolute',
            right: 9,
          }
        }}
        renderInput={params => (
          <TextField
            {...params}
            label="Selecione o evento"
            placeholder="Buscar evento..."
            variant="outlined"
            sx={{
              width: '100%',
              '& .MuiInputBase-root': {
                width: '100%',
                maxWidth: '100%',
                overflow: 'hidden',
              },
              '& .MuiOutlinedInput-input': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box
            component="li"
            {...props}
            sx={{
              width: '100%',
              overflow: 'hidden',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
                display: 'block',
              }}
            >
              {option.title}
            </Typography>
          </Box>
        )}
        noOptionsText={loading ? 'Carregando eventos...' : 'Nenhum evento encontrado'}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        ListboxProps={{
          style: {
            maxWidth: '500px',
          }
        }}
      />
    </Box>
  );
}
